/**
 * Forgetting, made to hold across TABS.
 *
 * `localStorage` is shared by every tab on this origin; the modules that guard
 * it were not. Each tab kept its own idea of what had been forgotten in module
 * memory, so a forget performed in one tab was a fact the others never learned —
 * and the others were still holding the thing that had just been deleted.
 *
 * The transcript case is the one that was reported: tab A forgets an
 * exploration while tab B is streaming an answer for it, and tab B's completion
 * writes `nv.chat.<id>` straight back. The document case underneath it is worse
 * and was found while fixing that one. `forgetSession` removes `nv.s.<id>` and
 * the library row; tab B still holds that exploration in memory, `autosave`
 * writes on session identity with a 1200ms debounce AND on `pagehide`, and
 * `writeDoc` re-adds the index entry. So tab B RESURRECTS the whole exploration
 * and its row in the library, and it does so merely by being closed. Fixing the
 * transcript alone would have made a conversation strictly harder to delete than
 * the graph it is about, which is the wrong way round — so both go through here.
 *
 * TWO MECHANISMS, because either alone is wrong:
 *
 *  - **A durable log**, which is what makes the guarantee true. A write is
 *    refused when the exploration's generation has moved since this tab started
 *    working from it. That holds even if no event ever arrives — a tab that was
 *    asleep, a listener that was never installed, a browser that batches.
 *  - **A `storage` listener**, which is what makes it timely. The log alone
 *    would let a tab go on showing a live conversation for an exploration that
 *    no longer exists until it next tried to write. The event drops the live
 *    copies at the moment it happens.
 *
 * A COUNTER per id rather than a set of dead ids, for the reason
 * `core/conversations.ts` gives about its own epochs: `s_demo` is a fixed id
 * that is deliberately reused, so a permanent "this id is dead" mark would make
 * the replayed worked example unwritable forever. What needs refusing is a write
 * from a tab that started BEFORE a forget, which is a question about that tab's
 * baseline and not about the id.
 *
 * A forget-all bumps one shared counter instead of every id, so the door that
 * promises everything costs one write and still invalidates explorations this
 * tab has never heard of.
 *
 * Imports nothing, like the other leaves that sit under both `persist.ts` and
 * the storage modules beside it.
 */

/**
 * ONE KEY PER FORGOTTEN EXPLORATION, plus one for the forget-everything door.
 *
 * The first version kept a single aggregated blob and read-modify-wrote it.
 * `localStorage` gives no atomicity across tabs, so two tabs forgetting two
 * different explorations in the same instant each wrote back a copy that never
 * contained the other's — and the loser's marker was gone, which is exactly the
 * invalidation a third tab needed in order not to resurrect it. `archive.ts`
 * had already met this and answered it the same way, in a comment this module
 * should have read first: one key per document, "so that two tabs mirroring two
 * explorations cannot race over one aggregated row".
 *
 * Independent keys make the write for one exploration structurally incapable of
 * being a write for another.
 *
 * Two tabs racing on the SAME id was called harmless here, on the grounds that
 * both were recording the same fact and a count advancing by one instead of two
 * still advances. That is true of every id except the one this app reuses. Both
 * tabs read `n` and both write `n + 1`; a third tab replaying the demo between
 * those two writes baselines `n + 1`, and the second forget — which is forgetting
 * a DIFFERENT, replayed document — then leaves the record where that third tab
 * already is, so its autosave puts the replay straight back.
 *
 * So a record is a TOKEN, not a count, and staleness is inequality rather than
 * increase. Two concurrent forgets write two different tokens whichever order
 * they land in, and any change at all reads as "not what I baselined against",
 * which is the actual question. The count is kept inside the token because it
 * makes the shelf readable in a debugger, and is never compared.
 */
const ALL_KEY = 'nv.forgot'
const ID_KEY = (id: string) => `nv.forgot.${id}`

/** No record at all, as a value that can be compared like any other. */
const NONE = ''

function readMark(key: string): string {
  try {
    return localStorage.getItem(key) ?? NONE
  } catch {
    // No store at all. An unreadable record means no forget is known, which
    // fails toward writing — refusing every write on a storage error would lose
    // work nobody asked to lose.
    return NONE
  }
}

function writeMark(key: string, value: string): boolean {
  try {
    localStorage.setItem(key, value)
    return true
  } catch {
    return false
  }
}

/**
 * The next token for a record, distinct from any other tab's.
 *
 * The leading count is for a human reading the shelf; nothing compares it. What
 * makes two concurrent forgets distinguishable is the suffix, which is why it is
 * here at all.
 */
let seq = 0
function nextMark(current: string): string {
  const n = Number(current.split('.')[0])
  const base = Number.isFinite(n) && n > 0 ? n : 0
  return `${base + 1}.${(++seq).toString(36)}${Math.random().toString(36).slice(2, 8)}`
}

/**
 * Where an exploration stands, as a PAIR rather than a sum.
 *
 * Summing them was wrong, and wrong in the direction that matters. Forget-all
 * bumps the global count, and it used to clear the per-exploration ones to stop
 * them accumulating — so an id already forgotten once went from `0 + 1` to
 * `1 + 0`. The same number. The door that promises the most invalidated nothing,
 * and only for explorations that had been forgotten before, which is the subset
 * most likely to be forgotten again.
 *
 * Compared arm by arm, neither can mask the other. The clearing is gone as well
 * — see `markForgotten` — because a record that goes down is unsafe under a
 * second writer whatever it is compared against; the pair is what makes the
 * accumulated records harmless, not what makes clearing them safe.
 */
interface Mark {
  all: string
  own: string
}

function markOf(id: string): Mark {
  return { all: readMark(ALL_KEY), own: readMark(ID_KEY(id)) }
}

/** What this tab believes about each exploration it has touched. */
const working = new Map<string, Mark>()
// SEEDED from the shelf, not empty. The records accumulate deliberately, so a
// tab starts up among historical ones — and an empty baseline would diff every
// one of them against zero and announce the lot on the first event of any kind.
// Downstream that is not cosmetic: `conversations.ts` bumps its epoch and drops
// the live copies, so forgetting one unrelated exploration in another tab would
// abort and erase a conversation open on a different one entirely.
let lastSeen = recordedIds()
let lastAll = readMark(ALL_KEY)

/** Every exploration this origin currently holds a forget record for. */
function recordedIds(): Map<string, string> {
  const out = new Map<string, string>()
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i)
      if (k && k.startsWith(`${ALL_KEY}.`)) out.set(k.slice(ALL_KEY.length + 1), readMark(k))
    }
  } catch {
    /* nothing to enumerate */
  }
  return out
}

/**
 * Take the baseline for an exploration this tab is about to work from.
 *
 * Called where a tab picks something up — reading a document, or a sender
 * taking its epoch — so that a forget landing afterwards is visibly afterwards.
 * Idempotent: the FIRST sighting is the one that counts, because that is the
 * moment the tab's copy stopped being re-read from disk.
 */
export function noteWorkingFrom(id: string): void {
  if (!working.has(id)) working.set(id, markOf(id))
}

/**
 * Start again from a BRAND-NEW document under an id this tab has seen before.
 *
 * `noteWorkingFrom` is idempotent on purpose — the first sighting is the moment
 * the tab's copy stopped being re-read from disk — and that is right for every
 * id except the one this app deliberately reuses. Once another tab has forgotten
 * `s_demo`, this tab's baseline can never move again, so replaying the worked
 * example gives a fresh document whose every transcript write is refused for the
 * life of the tab. The same trap as the tombstone and the summed generations,
 * arriving a third time by a different road.
 *
 * Called only where a new document is INSTALLED under a known id — not where an
 * existing in-memory copy is written, which would be laundering the forgotten
 * thing back into legitimacy.
 *
 * A RECORD STILL OWED HAS TO BE SETTLED HERE, on the near side of the baseline,
 * and that is the same trap arriving by yet another road. A record refused at
 * quota is kept and retried later; a record that lands AFTER this baseline reads
 * as "forgotten since you picked it up", so the replayed worked example is
 * condemned by a forget that predates it — every save refused for the life of the
 * tab, and other tabs told to drop its live conversation. The forget was real and
 * so is the new document; what is wrong is a record that cannot name which
 * generation it is about arriving on the wrong side of a line.
 *
 * So: publish everything the origin will accept first, which keeps the forget
 * durable for tabs that still hold the old copy. Then ABANDON what it would not
 * accept for this id — that record is about the document being replaced, and one
 * that can only arrive too late is worse than none, because it invalidates the
 * wrong document.
 *
 * A forget-EVERYTHING still owed is not abandoned, because it is not about this
 * id alone. If the origin will not take it even here it stays pending, and
 * landing later it will condemn this document too. That needs an origin so full
 * that dropping every stored exploration has not made room for one short key.
 */
export function beginWorkingFrom(id: string): void {
  flushForgotten()
  pending.delete(ID_KEY(id))
  working.set(id, markOf(id))
}

/**
 * Where an exploration stands right now, for a caller about to read it.
 *
 * Paired with `markMoved` around a read: a document's bytes and the record that
 * invalidates them are two separate keys, and `localStorage` will not hand out
 * both at one instant. A forget arriving between them leaves a reader holding
 * bytes it believes are current, with a baseline already at the forgotten count
 * — after which nothing downstream can tell the difference.
 */
export function markNow(id: string): Mark {
  return markOf(id)
}

/** Whether the record moved while the caller was reading. See `markNow`. */
export function markMoved(id: string, was: Mark): boolean {
  return moved(markOf(id), was)
}

export type { Mark }

/**
 * Has this exploration been forgotten since this tab started working from it?
 *
 * Records the baseline when there is none, so a first sighting is never stale —
 * a tab cannot be blocked by a forget it was never in a position to race.
 */
export function forgottenElsewhere(id: string): boolean {
  const now = markOf(id)
  const was = working.get(id)
  if (was === undefined) {
    working.set(id, now)
    return false
  }
  return moved(now, was)
}

/**
 * Whether a forget somebody else performed actually reaches this exploration.
 *
 * The same question `forgottenElsewhere` asks, with both of its defaults
 * inverted, because a different caller is asking. That one is asked by a WRITER
 * about its own work, so a first sighting is recorded and reads as safe: a tab
 * must not be blocked by a race it was never in. This one is asked by the
 * ANNOUNCEMENT about an id somebody else is holding, so it records nothing, and
 * an id with no baseline reads as REACHED — a forget-all has to invalidate
 * explorations this tab has never heard of, which is the whole reason that door
 * bumps one shared record instead of every id.
 *
 * It exists because the two mechanisms at the top of this file were allowed to
 * disagree, and the listener was the stricter of the two. The durable log admits
 * a write from a tab that took its baseline AFTER the record landed — that is
 * `beginWorkingFrom`'s entire purpose, so a replayed worked example is not
 * condemned by a forget that predates it. The listener consulted no baseline at
 * all. So in the window between another tab's write and this tab's queued
 * `storage` event — the record already readable, the event not yet run — a
 * conversation begun in that window was discarded and its paid request aborted,
 * while `forgottenElsewhere` went on saying the write was fine. Money spent,
 * nothing kept, by the half of this module whose only job was to be prompt.
 *
 * The listener may be EARLIER than the log. It may not be stricter.
 */
export function reachedByForget(id: string): boolean {
  const was = working.get(id)
  return was === undefined || moved(markOf(id), was)
}

/**
 * A record that says something DIFFERENT from what this tab baselined against.
 *
 * Different, and still present. A record that has vanished is not a forget —
 * nobody forgets by deleting the evidence — and reading absence as staleness
 * would be the worst possible failure here: an origin cleared by the browser, or
 * by anything else that empties `localStorage`, would leave every exploration
 * this tab had touched permanently unwritable, silently. It fails toward writing,
 * for the reason `readMark` gives.
 */
function moved(now: Mark, was: Mark): boolean {
  return (now.all !== NONE && now.all !== was.all) || (now.own !== NONE && now.own !== was.own)
}

/**
 * Record a forget so every other tab can see it. `null` is "forget everything".
 *
 * Returns whether the record actually landed, and that return is not decoration:
 * at quota the write throws, and the caller then removes the document — freeing
 * exactly the room the record needed. Swallowing it there leaves every other tab
 * with no durable invalidation for a document that is already gone, which is the
 * one failure this module exists to prevent. See `flushForgotten`.
 *
 * The tab performing it is by definition not behind, so its own baseline moves
 * up with the record — otherwise the door would immediately refuse this tab's
 * own next write.
 */
/**
 * What this tab's last record for a key moved, so a deletion that is then
 * refused can put back exactly that and nothing else (`keepWorkingFrom`): the
 * token it wrote, whether it landed, and the sight and baselines it replaced.
 */
const lastOwn = new Map<
  string,
  {
    token: string
    landed: boolean
    seen: string | undefined
    working: Map<string, Mark> | null
    /** What the record held when this one was asked for — what a baseline taken
     *  while it stayed unwritten reads. */
    prior: string
  }
>()

export function markForgotten(id: string | null): boolean {
  if (id === null) {
    const prior = readMark(ALL_KEY)
    const next = nextMark(prior)
    const before = { seen: lastAll, working: new Map(working), prior }
    const ok = writeMark(ALL_KEY, next)
    lastOwn.set(ALL_KEY, { token: next, landed: ok, ...before })
    // The per-exploration records are LEFT ALONE, and that is deliberate even
    // though it means they accumulate. Clearing them here was a generation
    // moving BACKWARD, and no comparison makes that safe: another tab can write
    // its own record between this bump and the sweep, and the sweep then deletes
    // a record NEWER than the forget-all — after which neither count is greater
    // than a baseline taken in that window, and the exploration is resurrected.
    // A record must never read as unchanged when it has changed. See D39.
    notePending(ALL_KEY, next, ok)
    lastAll = next
    lastSeen = recordedIds()
    working.clear()
    return ok
  }
  const prior = readMark(ID_KEY(id))
  const next = nextMark(prior)
  const seen = lastSeen.get(id)
  const ok = writeMark(ID_KEY(id), next)
  lastOwn.set(ID_KEY(id), { token: next, landed: ok, seen, working: null, prior })
  notePending(ID_KEY(id), next, ok)
  lastSeen.set(id, next)
  working.set(id, markOf(id))
  return ok
}

/**
 * Records the origin refused, kept so they can be retried. ONE PER KEY.
 *
 * It was a single slot, justified as "a forget is a press, and the next press
 * supersedes an unwritten one". That is true only when both presses name the
 * same key, which is the forget-everything door alone. Across two explorations
 * they are two independent facts and the second cannot stand in for the first —
 * and the slot did not merely lose the first, it ERASED it on the way past: the
 * assignment ran on success as well, so forgetting A at quota and then
 * successfully forgetting B threw A's unwritten record away. Another tab still
 * holding A could then save it back, never having seen a generation move, which
 * is the one failure this module exists to prevent.
 *
 * Keyed by the storage key rather than by exploration so the forget-everything
 * record is one entry among them and needs no special case. Bounded by how many
 * forgets an origin has refused; each entry is a short key and a short token,
 * and every one that lands is removed.
 */
const pending = new Map<string, string>()

/** Remember a record the origin refused, or forget an older refusal for the same
 *  key — the write that just landed supersedes it. */
function notePending(key: string, value: string, ok: boolean): void {
  if (ok) pending.delete(key)
  else pending.set(key, value)
}

/**
 * A deletion that was REFUSED after its record was asked for (D331): this tab
 * goes on working from the exploration, and owes nobody a record of a forget
 * that did not happen.
 *
 * It puts back what THIS tab's record moved, and reads nothing new. A record the
 * origin refused at quota is still in `pending`, and every later `flushForgotten`
 * would publish it — the next Forget of something else, the shelf's own deletion,
 * a replay's baseline — and condemn the very exploration the refusal kept. It is
 * abandoned instead, as `beginWorkingFrom` abandons a record about a document
 * being replaced: a record no tab ever saw moves nothing backward (D39), and the
 * sight this tab took of it is put back. One that landed has told the other tabs
 * and cannot be taken back; this tab's baseline already stands on it.
 *
 * Re-reading the record here instead would absorb whatever stands now — another
 * tab's genuine forget of the same exploration that landed while this deletion
 * was in flight — and this tab would then write back what that tab forgot.
 *
 * Forget-everything also cleared every baseline this tab held; a refusal restores
 * them, standing on this tab's own record where it landed, so a forget another
 * tab makes afterwards is still behind them.
 *
 * A record refused at quota can land LATE: a later `flushForgotten` publishes it
 * while the deletion is still in flight, in the room another deletion freed. Then
 * it has told the other tabs, as one that landed at once has, and this tab's
 * baselines move onto it the same way. Read as never landed, this tab was left
 * behind its own record, and took it for another tab's forget of the exploration
 * the refusal kept: every save of it refused as forgotten elsewhere.
 */
export function keepWorkingFrom(id: string | null): void {
  const key = id === null ? ALL_KEY : ID_KEY(id)
  const own = lastOwn.get(key)
  lastOwn.delete(key)
  pending.delete(key)
  if (!own) return
  const landed = own.landed || readMark(key) === own.token
  const late = landed && !own.landed
  if (id === null) {
    if (!landed && lastAll === own.token) lastAll = own.seen ?? NONE
    // Baselines taken while it was still unwritten stand behind it.
    if (late)
      for (const [held, mark] of working)
        if (mark.all === own.prior) working.set(held, { all: own.token, own: mark.own })
    for (const [held, mark] of own.working ?? [])
      if (!working.has(held)) working.set(held, landed ? { all: own.token, own: mark.own } : mark)
    return
  }
  if (late) {
    const mark = working.get(id)
    if (mark?.own === own.prior) working.set(id, { all: mark.all, own: own.token })
  }
  if (!landed && lastSeen.get(id) === own.token) {
    if (own.seen === undefined) lastSeen.delete(id)
    else lastSeen.set(id, own.seen)
  }
}

/**
 * Try again to write the records the origin refused.
 *
 * Called after the caller has removed the document, which is what frees the
 * room. Writes the SAME token rather than incrementing again, so a retry is not
 * a second forget. Returns whether everything landed; each record that does is
 * dropped, so a partial success leaves only what is still owed.
 */
export function flushForgotten(): boolean {
  for (const [key, value] of [...pending]) if (writeMark(key, value)) pending.delete(key)
  return pending.size === 0
}

const watchers = new Set<(id: string | null) => void>()

/** Told when ANOTHER tab forgets — one id, or null for all of them. Never fired
 *  for a forget this tab performed itself, which the caller already knows about. */
export function onForgottenElsewhere(fn: (id: string | null) => void): () => void {
  watchers.add(fn)
  return () => {
    watchers.delete(fn)
  }
}

/**
 * Read the records again and say what moved since the last time we looked.
 *
 * Exported for the tests, which have no `storage` events to fire, and used for
 * the event that says the whole store was cleared.
 */
export function syncForgotten(): void {
  const all = readMark(ALL_KEY)
  const ids = recordedIds()
  const prevAll = lastAll
  const prev = lastSeen
  lastAll = all
  lastSeen = ids
  // A record that VANISHED is not a forget, by `moved`'s own rule: nobody forgets
  // by deleting the evidence. This announced one anyway, so another tab — or a
  // sign-out — clearing `localStorage` told every tab on the origin that
  // everything had been forgotten, and each dropped its whole library from
  // memory while every document stood untouched on the shelf. A missing record
  // establishes nothing (D331).
  if (all !== prevAll && all !== NONE) {
    for (const fn of [...watchers]) fn(null)
    return
  }
  for (const [id, mark] of ids) {
    if (mark !== (prev.get(id) ?? NONE)) for (const fn of [...watchers]) fn(id)
  }
}

// A `storage` event fires in every OTHER tab on this origin, which is exactly
// the audience. Guarded because this module is imported by code that runs under
// Node in the tests, where there is no window to listen on.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    // A null key means the whole store was cleared out from under us.
    if (e.key === null || e.key === ALL_KEY || e.key.startsWith(`${ALL_KEY}.`)) syncForgotten()
  })
}
